import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Track, TrackId } from 'types/track'
import {
  addNestedTracksToMain,
  checkNestedTracksQuota,
  getNestedTracksForNestingIntoAnother,
  updateNestedTracks,
} from '@disco/state-management/actions/NestingActions'
import { loadBatch } from '@disco/state-management/actions/PlaylistTrackActions'
import api from '@disco/api/server'
import { TRACK_NESTING } from '@disco/constants/FeatureTypes'
import {
  ALREADY_NESTED_TRACK,
  NESTED_TRACK_INTO_ANOTHER_TRACK,
  TOO_MANY_VERSIONS,
} from '@disco/constants/Nesting'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { unlinkTrackAndShiftPosition } from '@disco/utils/nesting'
import NestingValidationModal from 'components/common/nesting-validation-modal'
import DocumentHeadTags from 'components/document-head-tags'

const NestingValidation = (): React.ReactNode => {
  const isNestingEnabled = useFeatureEnabled(TRACK_NESTING)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location?.state
  const [isAwaiting, setIsAwaiting] = useState<boolean>(false)
  useEffect(() => {
    if (!locationState || !isNestingEnabled) navigate('/', { replace: true })
  }, [locationState, isNestingEnabled, dispatch])
  const type = locationState?.type
  const nestingLabel = locationState?.nestingLabel
  const track: Track = useSelector(state =>
    getTrack(state, {
      id: locationState?.track,
    }),
  )
  const versionOf: Track = useSelector(state =>
    getTrack(state, {
      id: locationState?.versionOf,
    }),
  )
  useEffect(() => {
    const loadTrack = async (id: TrackId) => {
      try {
        const track = await api.getTrack(id)
        dispatch(loadBatch([track]))
      } catch (error) {}
    }

    if (!versionOf && locationState.versionOf) {
      loadTrack(locationState.versionOf)
    }
  }, [dispatch, locationState.versionOf, versionOf])
  const newVersionOf: Track = useSelector(state =>
    getTrack(state, {
      id: locationState?.newVersionOf,
    }),
  )
  const duplicateTrack: Track = useSelector(state =>
    getTrack(state, {
      id: locationState?.duplicateTrack,
    }),
  )
  const nestedTracks: Track[] = locationState?.nestedTracks

  const handleCancel = () => {
    navigate('/', { replace: true })
  }

  const handleProceed = async () => {
    if (locationState.openLocation) {
      return navigate(locationState.openLocation.to, {
        state: locationState.openLocation.state,
        replace: true,
      })
    } else {
      navigate('/', { replace: true })
    }

    if (
      type === ALREADY_NESTED_TRACK &&
      track &&
      nestedTracks &&
      versionOf &&
      newVersionOf
    ) {
      setIsAwaiting(true)
      const tracks = await api.getNestedTracks(versionOf.id)
      const { itemsWithUpdatedFields } = unlinkTrackAndShiftPosition({
        track,
        tracks,
      })
      dispatch(updateNestedTracks(itemsWithUpdatedFields))
      dispatch(
        addNestedTracksToMain(
          newVersionOf,
          nestedTracks.map(item => {
            if (item.id === track.id) {
              return { ...item, ...itemsWithUpdatedFields[track.id] }
            }

            return item
          }),
          nestingLabel,
        ),
      )
    }

    if (
      type === NESTED_TRACK_INTO_ANOTHER_TRACK &&
      track &&
      nestedTracks &&
      newVersionOf
    ) {
      const isNestingOverQuota = checkNestedTracksQuota(
        newVersionOf,
        nestedTracks,
      )

      if (isNestingOverQuota) {
        navigate(`/explorer/tracks/${newVersionOf.id}/nesting-validation`, {
          state: {
            type: TOO_MANY_VERSIONS,
          },
        })
        return
      }

      const tracks = getNestedTracksForNestingIntoAnother(track, nestedTracks)
      dispatch(addNestedTracksToMain(newVersionOf, tracks, nestingLabel))
    }
  }

  return (
    <>
      <DocumentHeadTags title="Manage nesting tracks error | DISCO" />
      <NestingValidationModal
        isAwaiting={isAwaiting}
        type={type}
        track={track}
        versionOf={versionOf}
        newVersionOf={newVersionOf}
        duplicateTrack={duplicateTrack}
        onProceed={handleProceed}
        onCancel={handleCancel}
      />
    </>
  )
}

export default NestingValidation
